/**
 * List widget — records as a scrollable list rather than a grid of cells.
 *
 * Where the table answers "show me the columns", the list answers "show me the
 * things": one row per feature, a title line, an optional second line, and an
 * optional value pinned right. It is the natural shape for a work-order or
 * incident feed, and it fits a narrow tile that a table cannot.
 *
 * Like the table it can drive Filter actions: clicking a row broadcasts its
 * title value, and clicking the same row again clears the selection.
 */

import { useEffect, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useSourceFields } from './useSourceFields';
import { useWidgetData } from './useWidgetData';
import WidgetFrame from './WidgetFrame';
import {
  combineWhere,
  useIncomingConstraint,
  type ActionSourceProps,
  type ActionTargetProps,
  useOutgoingSignal,
} from './useActions';
import {
  getDataSourceAdapter,
  type CategoryResult,
  type RecordResult,
} from '../services/dashboardDataService';
import {
  buildWhereClause,
  formatCellValue,
  formatNumber,
  paletteColor,
  validateWidget,
} from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardWidget } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  source?: DashboardDataSource;
  refreshInterval: number;
  accentColor: string;
  dividerColor?: string;
  selectorWhere?: string;
}

/** Rows read per request. Deliberately modest: a list is meant to be scanned. */
const DEFAULT_MAX_ITEMS = 50;

export default function ListWidget({
  widget,
  source,
  refreshInterval,
  accentColor,
  dividerColor,
  selectorWhere,
  incomingSignals,
  onClearSignals,
  activeSignal,
  onSignal,
}: Props & ActionTargetProps & ActionSourceProps) {
  const { fields } = useSourceFields(source);
  const problems = validateWidget(widget);

  /* `grouped` collapses the list to one row per distinct value; `features`
     keeps one row per record. See WidgetConfig.listMode. */
  const grouped = widget.config.listMode === 'grouped';
  const titleField = widget.config.titleField ?? '';
  const subtitleField = widget.config.subtitleField ?? '';
  const valueField = widget.config.listValueField ?? '';
  const colorField = widget.config.listColorField ?? '';
  const maxItems = widget.config.maxItems ?? DEFAULT_MAX_ITEMS;
  const multiSelect = Boolean(widget.config.multiSelect);
  const searchEnabled = Boolean(widget.config.listSearch) && Boolean(titleField);

  /* Typed immediately, for the suggestion list; only reaches the query after
     a short pause, so scrolling through a fast typist's keystrokes does not
     mean one request per letter. */
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  useEffect(() => {
    const handle = window.setTimeout(() => setSearchTerm(searchInput), 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  /* The field's own distinct values, fetched once and filtered client-side as
     the user types — the suggestion list, same idea as a header Category
     selector's dropdown. */
  const [suggestionPool, setSuggestionPool] = useState<string[]>([]);
  useEffect(() => {
    if (!searchEnabled || !source) {
      setSuggestionPool([]);
      return undefined;
    }
    let active = true;
    void getDataSourceAdapter(source)
      .fetchDistinctValues(source, titleField)
      .then((values) => {
        if (active) setSuggestionPool(values);
      })
      .catch(() => {
        if (active) setSuggestionPool([]);
      });
    return () => {
      active = false;
    };
  }, [searchEnabled, source, titleField]);
  const suggestions = searchInput.trim()
    ? suggestionPool
        .filter((entry) => entry.toLowerCase().includes(searchInput.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const searchWhere =
    searchEnabled && searchTerm.trim()
      ? `${titleField} LIKE '%${searchTerm.trim().replace(/'/g, "''")}%'`
      : '';

  const incoming = useIncomingConstraint(incomingSignals, fields);
  const where = combineWhere(
    combineWhere(combineWhere(buildWhereClause(widget.filters, fields), incoming.where), selectorWhere ?? ''),
    searchWhere,
  );
  /* Selections leave through here so a spatially-matched action can attach
     the selected shape. Attribute-matched actions pass straight through. */
  const emitSignal = useOutgoingSignal(widget, source, onSignal, where);
  const enabled = Boolean(source) && problems.length === 0;

  // Only the fields actually shown are requested — a list of 50 rows should not
  // drag every column of a wide layer across the network.
  const columns = Array.from(
    new Set([titleField, subtitleField, valueField, colorField].filter(Boolean)),
  );

  const statistic = widget.config.statistic ?? 'count';

  /* One hook, two shapes. Grouping reuses `fetchCategories` — the same call the
     pie and bar charts make — so ArcGIS still groups server-side and GeoServer
     still reports when its client-side grouping was capped. */
  const { data, status, error, reload } = useWidgetData<RecordResult | CategoryResult>(
    () => {
      const adapter = getDataSourceAdapter(source as DashboardDataSource);
      if (grouped) {
        return adapter.fetchCategories(source as DashboardDataSource, {
          categoryField: titleField,
          statistic,
          field: widget.config.field,
          where,
          extent: incoming.extent, geometry: incoming.geometry,
          limit: maxItems,
          sortBy: widget.config.sortBy,
        });
      }
      return adapter.fetchRecords(source as DashboardDataSource, {
        columns,
        where,
        extent: incoming.extent, geometry: incoming.geometry,
        limit: maxItems,
        orderByField: widget.config.orderByField,
        orderDirection: widget.config.orderDirection,
      });
    },
    `list|${grouped ? 'grouped' : 'features'}|${source?.id}|${
      grouped ? `${titleField}|${statistic}|${widget.config.field ?? ''}|${widget.config.sortBy ?? ''}` : columns.join(',')
    }|${where}|${JSON.stringify(incoming.extent ?? null)}|${incoming.geometry ? 'geom' : ''}|${maxItems}|${
      widget.config.orderByField ?? ''
    }|${widget.config.orderDirection ?? ''}`,
    refreshInterval,
    enabled,
  );

  /* Normalise both shapes into the rows the list draws, so the markup below
     does not branch on the mode. */
  const categoryData = grouped ? ((data as CategoryResult | null)?.data ?? []) : [];
  const recordData = grouped ? [] : ((data as RecordResult | null)?.records ?? []);

  const rows: { key: string; title: string; subtitle: string; value: string; colorKey: string }[] =
    grouped
      ? categoryData.map((entry) => ({
          key: entry.category,
          title: entry.category,
          subtitle: '',
          value: formatNumber(entry.value, widget.config.decimals ?? 0),
          colorKey: entry.category,
        }))
      : recordData.map((record, index) => ({
          key: String(index),
          title: formatCellValue(record[titleField]),
          subtitle: subtitleField ? formatCellValue(record[subtitleField]) : '',
          value: valueField ? formatCellValue(record[valueField]) : '',
          colorKey: colorField ? formatCellValue(record[colorField]) : '',
        }));

  const total = grouped ? null : ((data as RecordResult | null)?.total ?? null);
  const truncated = grouped ? Boolean((data as CategoryResult | null)?.truncated) : false;

  const canSelect =
    Boolean(onSignal) &&
    Boolean(titleField) &&
    (widget.actions ?? []).some(
      (action) => action.trigger === 'selection' && action.targetWidgetIds.length > 0,
    );
  const selectedValues = new Set(
    activeSignal && activeSignal.kind === 'selection'
      ? activeSignal.values && activeSignal.values.length > 0
        ? activeSignal.values
        : [activeSignal.value]
      : [],
  );

  const selectRow = (title: string) => {
    // `(blank)` is the bucket the grouper puts nulls in; the runtime turns an
    // empty value into `IS NULL`, which is what actually matches them.
    const value = title === '(blank)' ? '' : title;

    if (!multiSelect) {
      if (selectedValues.has(value)) {
        onSignal?.(widget.id, null);
        return;
      }
      emitSignal(widget.id, {
        kind: 'selection',
        sourceId: widget.id,
        sourceTitle: widget.title,
        field: titleField,
        value,
      });
      return;
    }

    const next = new Set(selectedValues);
    if (next.has(value)) next.delete(value);
    else next.add(value);

    if (next.size === 0) {
      onSignal?.(widget.id, null);
      return;
    }
    const nextValues = [...next];
    emitSignal(widget.id, {
      kind: 'selection',
      sourceId: widget.id,
      sourceTitle: widget.title,
      field: titleField,
      value: nextValues[0],
      values: nextValues,
    });
  };

  /* A stable colour per distinct value, so the same status is always the same
     dot — index-based colouring would reshuffle as rows are filtered. */
  const colorFor = (key: string, index: number) => {
    if (!key) return accentColor;
    let hash = 0;
    for (let position = 0; position < key.length; position += 1) {
      hash = (hash * 31 + key.charCodeAt(position)) | 0;
    }
    return paletteColor(widget.config.palette, Math.abs(hash) || index);
  };

  /* Rows mean different things in the two modes, so the footnote says which. */
  const footnoteParts = grouped
    ? [
        source?.name,
        `${rows.length} value${rows.length === 1 ? '' : 's'} by ${titleField}`,
        truncated ? 'partial — first 5,000 records' : '',
      ].filter(Boolean)
    : [
        source?.name,
        total !== null ? `${total.toLocaleString()} records` : '',
        total !== null && total > rows.length ? `showing first ${rows.length}` : '',
      ].filter(Boolean);

  return (
    <WidgetFrame
      title={widget.title}
      status={status}
      error={error}
      onRetry={reload}
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      actionLabels={incoming.labels}
      onClearActions={() => onClearSignals?.(incoming.sourceIds)}
      footnote={footnoteParts.join(' · ')}
    >
      <div className="flex h-full flex-col">
        {searchEnabled && (
          <div className="relative flex-shrink-0 border-b border-slate-100 px-2 py-1.5">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onFocus={() => setSuggestionsOpen(true)}
              onBlur={() => window.setTimeout(() => setSuggestionsOpen(false), 120)}
              placeholder={`Search ${titleField}…`}
              className="w-full rounded-md border border-slate-200 bg-slate-50 py-1 pl-6 pr-2 text-[11px] text-slate-700 outline-none focus:border-blue-400 focus:bg-white"
            />
            {suggestionsOpen && suggestions.length > 0 && (
              <ul className="absolute left-2 right-2 top-full z-10 mt-1 max-h-36 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg">
                {suggestions.map((entry) => (
                  <li key={entry}>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setSearchInput(entry);
                        setSuggestionsOpen(false);
                      }}
                      className="block w-full truncate px-2 py-1 text-left text-[11px] text-slate-700 hover:bg-slate-50"
                    >
                      {entry}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {rows.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <p className="text-xs text-slate-400">No records match</p>
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto">
            {rows.map((row, index) => {
              // A blank group is broadcast as '', so compare on the same footing.
              const selectionValue = row.title === '(blank)' ? '' : row.title;
              const isSelected = canSelect && selectedValues.has(selectionValue);

              return (
                <li key={`${row.key}-${index}`}>
                  <div
                    role={canSelect ? 'button' : undefined}
                    tabIndex={canSelect ? 0 : undefined}
                    aria-pressed={canSelect ? isSelected : undefined}
                    onClick={canSelect ? () => selectRow(row.title) : undefined}
                    onKeyDown={
                      canSelect
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              selectRow(row.title);
                            }
                          }
                        : undefined
                    }
                    className={`flex items-center gap-2.5 border-b border-slate-100 px-3 py-2 ${
                      canSelect ? 'cursor-pointer hover:bg-slate-50' : ''
                    } ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    {multiSelect && canSelect && (
                      <span
                        aria-hidden="true"
                        className={`flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border ${
                          isSelected ? 'border-blue-600 bg-blue-600' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                      </span>
                    )}
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: colorFor(row.colorKey, index) }}
                    />
                    <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-[13px] font-medium text-slate-800"
                      title={row.title}
                    >
                      {row.title}
                    </span>
                    {row.subtitle && (
                      <span className="block truncate text-[11px] text-slate-500">
                        {row.subtitle}
                      </span>
                    )}
                  </span>
                  {row.value && (
                    <span className="flex-shrink-0 text-[12px] font-semibold text-slate-700">
                      {row.value}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
          </ul>
        )}
      </div>
    </WidgetFrame>
  );
}
